import React, { useEffect, useRef } from "react";
import "./index.css";
import {
  AnimatePresence,
  motion,
  useAnimation,
  useInView,
} from "framer-motion";

import FloatingSymbols from "./components/FloatingSymbols";
import backgroundImage from "./assets/Mainbackground.webp";
import cedarImage from "./assets/cedar.webp";
import areaImage from "./assets/Area.webp";
import cupImage from "./assets/cup.webp";
import feiruz from "./assets/feiruz.webp";
import cupSymbol from "./assets/cup-symbol.webp";
import belowTemple from "./assets/SVG/belowTemples.svg";
import HeroSection from "./components/HeroSection";
import PortfolioChatbot from "./components/PortfolioChatbot";

function AnimatedSection({ children, className, variants }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) controls.start("visible");
  }, [inView, controls]);

  return (
    <motion.section
      ref={ref}
      className={className}
      initial="hidden"
      animate={controls}
      variants={variants}
    >
      {children}
    </motion.section>
  );
}

function ExperienceCards() {
  const [active, setActive] = React.useState("intern"); // "intern" | "training"

  const cardVariants = {
    initial: (direction) => ({
      opacity: 0,
      y: direction === "next" ? 24 : -24,
    }),
    animate: { opacity: 1, y: 0, transition: { duration: 0.35 } },
    exit: (direction) => ({
      opacity: 0,
      y: direction === "next" ? -24 : 24,
      transition: { duration: 0.25 },
    }),
  };

  const isIntern = active === "intern";

  return (
    <div className="relative">
      {/* Toggle controls */}
      <div className="flex items-center justify-center gap-2 sm:gap-3 mb-4">
        <button
          onClick={() => setActive("intern")}
          className={`px-4 py-2 rounded-full border transition text-sm sm:text-base
            ${isIntern
              ? "bg-[#b88c5a] text-white border-[#b88c5a]"
              : "bg-white/60 text-[#4e5d4e] border-[#d4c2ae] hover:bg-white/80"
            }`}
        >
          Internship
        </button>

        <button
          onClick={() => setActive("training")}
          className={`px-4 py-2 rounded-full border transition text-sm sm:text-base
            ${!isIntern
              ? "bg-[#b88c5a] text-white border-[#b88c5a]"
              : "bg-white/60 text-[#4e5d4e] border-[#d4c2ae] hover:bg-white/80"
            }`}
        >
          Training
        </button>
      </div>

      {/* Animated card replace */}
      <AnimatePresence mode="wait" custom={isIntern ? "next" : "prev"}>
        {isIntern ? (
          <motion.div
            key="intern"
            className="bg-white/80 backdrop-blur-sm rounded-2xl border border-[#d4c2ae] shadow-lg p-6 sm:p-8"
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            custom="next"
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <h3 className="text-xl sm:text-2xl font-heritage-bold text-[#7c5e3c]">
                  Web & Mobile Development Intern
                </h3>
                <p className="text-[#4e5d4e] text-sm sm:text-base mt-1">
                  Creoshift Lebanon
                </p>
              </div>
              <span className="text-sm sm:text-base text-[#4e5d4e] bg-white/60 border border-[#d4c2ae] rounded-full px-4 py-1 w-fit">
                Oct 2024 – Jul 2025
              </span>
            </div>

            <ul className="mt-5 space-y-3 text-[#4e5d4e] text-base sm:text-lg leading-7">
              <li className="flex gap-3">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-[#b88c5a] shrink-0" />
                Completed a 9-month internship building responsive web platforms and cross-platform mobile applications.
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-[#b88c5a] shrink-0" />
                Architected interactive user interfaces using React, Next.js, Tailwind CSS, GSAP, and React Native.
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-[#b88c5a] shrink-0" />
                Collaborated within cross-functional agile teams, using Git version control and modern software-development workflows.
              </li>
            </ul>

            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setActive("training")}
                className="text-[#b88c5a] hover:opacity-80 transition text-sm sm:text-base"
              >
                View training experience →
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="training"
            className="bg-white/80 backdrop-blur-sm rounded-2xl border border-[#d4c2ae] shadow-lg p-6 sm:p-8"
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            custom="prev"
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <h3 className="text-xl sm:text-2xl font-heritage-bold text-[#7c5e3c]">
                  Mobile Development Trainee
                </h3>
                <p className="text-[#4e5d4e] text-sm sm:text-base mt-1">
                  Creoshift Lebanon
                </p>
              </div>
              <span className="text-sm sm:text-base text-[#4e5d4e] bg-white/60 border border-[#d4c2ae] rounded-full px-4 py-1 w-fit">
                Jul 2024 – Sep 2024
              </span>
            </div>

            <ul className="mt-5 space-y-3 text-[#4e5d4e] text-base sm:text-lg leading-7">
              <li className="flex gap-3">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-[#b88c5a] shrink-0" />
                Completed a two-month engineering track focused entirely on mobile application frameworks.
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-[#b88c5a] shrink-0" />
                Engineered mobile applications in React Native using modular, component-based architectures.
              </li>
            </ul>

            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setActive("intern")}
                className="text-[#b88c5a] hover:opacity-80 transition text-sm sm:text-base"
              >
                ← Back to internship
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function App() {
  const [ready, setReady] = React.useState(false);

  useEffect(() => {
    let cancelled = false;

    const preloadImage = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve;
        img.src = src;
      });

    const waitForFonts = async () => {
      // Wait for fonts (supported on modern browsers)
      if (document.fonts?.ready) {
        try {
          await document.fonts.ready;
        } catch { }
      } else {
        // Fallback: small delay
        await new Promise((r) => setTimeout(r, 300));
      }
    };

    (async () => {
      const imagesToLoad = [
        backgroundImage,
        cedarImage,
        areaImage,
        cupImage,
        feiruz,
        cupSymbol,
        belowTemple,
        "/jad-profile.jpg",
        "/symbols/pattern1.svg",
        "/symbols/pattern3.svg",
        "/symbols/pattern4.svg",
        "/symbols/pattern5.svg",
        "/symbols/pattern6.svg",
        "/symbols/pattern7.svg",
      ];

      const imagePromises = imagesToLoad.map(preloadImage);
      await Promise.all([waitForFonts(), ...imagePromises]);
      if (!cancelled) setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const skillGroups = [
    {
      title: "Languages",
      subtitle: "Core programming",
      items: ["Python", "Java", "JavaScript", "SQL", "Kotlin", "C/C++"],
    },
    {
      title: "ML & AI",
      subtitle: "Data & machine learning",
      items: ["Scikit-learn", "Pandas", "NumPy", "K-Means", "Apriori", "TF-IDF"],
    },
    {
      title: "Frontend",
      subtitle: "UI, animations & modern web",
      items: ["React", "Next.js", "React Native", "Tailwind CSS", "GSAP", "HTML/CSS"],
    },
    {
      title: "Backend",
      subtitle: "APIs, data & services",
      items: ["Node.js", "Express.js", "REST APIs", "MongoDB", "MySQL", "Firebase", "PHP"],
    },
    {
      title: "Mobile",
      subtitle: "Cross-platform & native basics",
      items: ["React Native", "Kotlin", "Java"],
    },
    {
      title: "Tools",
      subtitle: "Workflow & collaboration",
      items: ["Git", "GitHub", "GitLab", "Docker", "Figma", "VS Code"],
    },
    {
      title: "Cybersecurity",
      subtitle: "Network & web assessment",
      items: ["Nmap", "Nuclei", "OWASP ZAP", "ffuf", "JWT", "DVWA"],
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
  };
  if (!ready) {
    return (
      <div
        className="h-screen w-screen flex items-center justify-center bg-[#f1ece6]"
        style={{ fontFamily: "Goudy Trajan, serif" }}
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-16 h-1 bg-[#b88c5a] rounded-full" />
          <p className="text-[#7c5e3c] text-lg">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <main
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
      }}
      className="h-screen ios-h-screen overflow-y-scroll snap-y snap-mandatory font-heritage-bold relative"
    >
      {/* DO NOT TOUCH HERO */}
      <HeroSection />
      <PortfolioChatbot />

      {/* ABOUT SECTION */}
      <section id="about" className="min-h-screen snap-start px-4 sm:px-6 lg:px-10 py-14 sm:py-16 relative z-10 bg-white/30 backdrop-blur-sm flex flex-col">
        <FloatingSymbols />

        {/* Header (divider ALWAYS at top) */}
        <div className="flex flex-col items-center">
          <div className="w-16 h-1 bg-[#b88c5a] rounded-full mb-4" />
          <h2 className="text-3xl sm:text-4xl font-heritage-bold text-[#7c5e3c] mb-4 text-center">
            About Me
          </h2>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center">
          <motion.p
            className="text-base sm:text-lg leading-7 sm:leading-8 text-[#4e5d4e] text-center max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            Computer Science graduate with a strong foundation in software
            development, machine learning, and artificial intelligence. I build
            robust web and mobile applications through industry training and
            internships, and enjoy using data science and AI to solve complex,
            real-world engineering challenges.
          </motion.p>
        </div>
      </section>

      {/* EDUCATION SECTION */}
      <section id="education" className="min-h-screen snap-start px-4 sm:px-6 lg:px-10 py-14 sm:py-16 relative z-10 bg-[#f1ece6]/90 flex items-center">
        <div className="w-full max-w-5xl mx-auto">
          <div className="flex flex-col items-center mb-9">
            <div className="w-16 h-1 bg-[#b88c5a] rounded-full mb-4" />
            <h2 className="text-3xl sm:text-4xl font-heritage-bold text-[#7c5e3c] text-center">Education</h2>
          </div>
          <div className="space-y-5">
            <div className="bg-white/90 border border-[#d4c2ae] rounded-2xl shadow-lg p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-start">
                <div><h3 className="text-xl sm:text-2xl text-[#7c5e3c]">Master’s in Computer Science</h3><p className="text-[#4e5d4e] mt-1">American University of Beirut (AUB)</p></div>
                <span className="text-sm text-[#b88c5a] shrink-0">Present · Expected 2028</span>
              </div>
              <p className="mt-4 text-[#4e5d4e] leading-7">Currently pursuing graduate studies in Computer Science.</p>
            </div>
            <div className="bg-white/90 border border-[#d4c2ae] rounded-2xl shadow-lg p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-start">
                <div><h3 className="text-xl sm:text-2xl text-[#7c5e3c]">Master 1 in Computer Science</h3><p className="text-[#4e5d4e] mt-1">Lebanese University · Faculty of Sciences</p></div>
                <span className="text-sm text-[#b88c5a] shrink-0">Completed 2026</span>
              </div>
            </div>
            <div className="bg-white/90 border border-[#d4c2ae] rounded-2xl shadow-lg p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-start">
                <div><h3 className="text-xl sm:text-2xl text-[#7c5e3c]">Bachelor’s Degree in Computer Science</h3><p className="text-[#4e5d4e] mt-1">Lebanese University · Faculty of Sciences · GPA: 4.0/4.0</p></div>
                <span className="text-sm text-[#b88c5a] shrink-0">Graduated 2025</span>
              </div>
              <p className="mt-4 border-l-4 border-[#b88c5a] pl-4 text-[#4e5d4e] leading-7">Ranked 1st in the Faculty of Sciences for three consecutive academic years, graduating with a perfect GPA of 4.0/4.0.</p>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCE SECTION */}
      <section id="experience" className="min-h-screen snap-start px-4 sm:px-6 lg:px-10 py-14 sm:py-16 relative z-10 bg-white/40 backdrop-blur-md flex flex-col">
        {/* Header */}
        <div className="flex flex-col items-center mb-4 shrink-0">
          <div className="w-16 h-1 bg-[#b88c5a] rounded-full mb-3" />
          <motion.h2
            className="text-3xl sm:text-4xl font-heritage-bold text-[#7c5e3c] mb-1 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            Experience
          </motion.h2>
          <motion.p
            className="text-center text-[#b88c5a] text-sm sm:text-lg max-w-2xl"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.06 }}
          >
            Internship & training experience
          </motion.p>
        </div>

        {/* Content */}
        <div className="flex-1 mt-4 flex items-start justify-center">
          <motion.div
            className="w-full max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.12 }}
          >
            <ExperienceCards />
          </motion.div>
        </div>
      </section>

      {/* SKILLS SECTION */}
      <section id="skills" className="min-h-screen snap-start bg-[#f1ece6] px-4 sm:px-6 lg:px-10 py-14 sm:py-16 relative z-10 flex flex-col">
        {/* Header */}
        <div className="flex flex-col items-center mb-4 shrink-0">
          <div className="w-16 h-1 bg-[#b88c5a] rounded-full mb-3" />

          <motion.h2
            className="text-3xl sm:text-4xl font-heritage-bold text-[#7c5e3c] mb-1 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            Skills
          </motion.h2>

          <motion.p
            className="text-center text-[#b88c5a] text-sm sm:text-lg max-w-2xl"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.06 }}
          >
            A quick overview of what I use for frontend, mobile, backend, and my
            workflow
          </motion.p>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center py-4">
          <div className="w-full max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
              {skillGroups.map((group, idx) => (
                <motion.div
                  key={group.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                >
                  <div className="bg-white/90 rounded-2xl shadow-lg border border-[#d4c2ae] p-5 sm:p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-200 h-full">
                    <div className="text-center mb-4">
                      <h3 className="text-base sm:text-xl font-heritage-bold text-[#7c5e3c]">
                        {group.title}
                      </h3>
                      <p className="text-xs sm:text-base text-[#b88c5a] mt-1">
                        {group.subtitle}
                      </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-2">
                      {group.items.map((item) => (
                        <span
                          key={item}
                          className="px-3 py-1 rounded-full bg-white/70 border border-[#d4c2ae] text-[#4e5d4e] text-xs sm:text-base hover:bg-white transition"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Summary (still 100vh-safe) */}
            <motion.div
              className="mt-4 flex justify-center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.6,
                delay: (skillGroups.length - 1) * 0.1 + 0.2,
              }}
            >
              <div className="bg-white/70 border border-[#d4c2ae] rounded-2xl px-5 py-3 max-w-3xl text-center shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
                <p className="text-[#4e5d4e] text-xs sm:text-base leading-6">
                  I’m strongest in building responsive UIs, smooth animations,
                  and clean component architecture — with experience in both web
                  and mobile environments.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROJECTS SECTION */}
      <section id="projects" className="min-h-screen snap-start px-4 sm:px-6 lg:px-10 py-14 sm:py-16 relative z-10 bg-white/50 backdrop-blur-sm flex items-center">
        <div className="w-full max-w-5xl mx-auto">
          <div className="flex flex-col items-center mb-9">
            <div className="w-16 h-1 bg-[#b88c5a] rounded-full mb-4" />
            <h2 className="text-3xl sm:text-4xl font-heritage-bold text-[#7c5e3c] text-center">Projects</h2>
          </div>
          <div className="bg-white/90 rounded-2xl border border-[#d4c2ae] shadow-lg p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
              <div><h3 className="text-xl sm:text-2xl text-[#7c5e3c]">Machine Learning Models Repository</h3><p className="text-[#b88c5a] mt-1">Python · Scikit-learn · Pandas · NumPy</p></div>
              <span className="text-sm text-[#4e5d4e] bg-[#f1ece6] rounded-full px-4 py-1 h-fit w-fit">Open source</span>
            </div>
            <ul className="mt-6 space-y-3 text-[#4e5d4e] leading-7 list-disc pl-5">
              <li>Developed a comprehensive collection of machine learning implementations across regression, classification, clustering, and association-rule learning.</li>
              <li>Implemented and evaluated regression models including Linear, Polynomial, SVR, Decision Trees, and Random Forests.</li>
              <li>Built classification models including Logistic Regression, KNN, SVM, and Naïve Bayes.</li>
            </ul>
          </div>
          <div className="mt-5 bg-white/90 rounded-2xl border border-[#d4c2ae] shadow-lg p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
              <div><h3 className="text-xl sm:text-2xl text-[#7c5e3c]">CyberGuard — Network Vulnerability Monitoring System</h3><p className="text-[#b88c5a] mt-1">React · Tailwind CSS · Node.js · Express · MongoDB · Mongoose</p></div>
              <span className="text-sm text-[#4e5d4e] bg-[#f1ece6] rounded-full px-4 py-1 h-fit w-fit">Team project</span>
            </div>
            <ul className="mt-6 space-y-3 text-[#4e5d4e] leading-7 list-disc pl-5">
              <li>Built a unified dashboard for network discovery, vulnerability monitoring, findings, and policy management.</li>
              <li>Contributed across the full stack, from the React and Tailwind interface to the Node.js, Express, MongoDB, and Mongoose backend.</li>
              <li>Worked with Nmap, Nuclei, OWASP ZAP, and ffuf for asset discovery and web and network vulnerability assessment, alongside authentication, audit logs, role controls, notifications, and backup workflows.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* FOOTER (instead of contact page) */}
      <footer id="contact" className="snap-start bg-[#fdfaf6] px-4 sm:px-6 lg:px-10 py-10 relative z-10">
        <div className="w-full max-w-5xl mx-auto">
          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-16 h-1 bg-[#b88c5a] rounded-full mb-2" />
            <p className="text-[#7c5e3c] text-lg sm:text-xl font-heritage-bold">
              Jad Mcheik
            </p>
            <p className="text-[#4e5d4e] text-sm sm:text-base">
              Software Developer • Web / Mobile / Machine Learning
            </p>
            <p className="text-[#4e5d4e] text-sm sm:text-base">Beirut, Lebanon</p>

            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 mt-2">
              <span className="hidden sm:inline text-[#d4c2ae]">•</span>
              <a
                href="mailto:jad.m.mcheik@gmail.com"
                className="text-[#b88c5a] hover:opacity-80 transition text-sm sm:text-base"
              >
                jad.m.mcheik@gmail.com
              </a>
              <span className="hidden sm:inline text-[#d4c2ae]">•</span>
              <a href="https://github.com/jadmcheik" target="_blank" rel="noreferrer" className="text-[#b88c5a] hover:opacity-80 transition text-sm sm:text-base">
                github.com/jadmcheik
              </a>
              <span className="hidden sm:inline text-[#d4c2ae]">•</span>
              <a href="https://www.linkedin.com/in/jadmcheik" target="_blank" rel="noreferrer" className="text-[#b88c5a] hover:opacity-80 transition text-sm sm:text-base">
                LinkedIn
              </a>
            </div>

            <a href="https://jadmcheik.com" target="_blank" rel="noreferrer" className="text-[#b88c5a] hover:opacity-80 transition text-sm sm:text-base">jadmcheik.com</a>
            <a href="/Jad-Mcheik-CV.docx" download className="text-[#b88c5a] hover:opacity-80 transition text-sm sm:text-base">Download CV</a>

            <p className="text-[#4e5d4e] text-sm sm:text-base">Arabic: Native · English: Fluent · French: Intermediate</p>

            <p className="text-[#4e5d4e]/80 text-xs sm:text-sm mt-4">
              © {new Date().getFullYear()} Jad Mcheik. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}

export default App;
